import { type FilterState, describeActiveRange } from './dashboardFilterUtils';
import { useDashboardFilters } from './useDashboardFilters';
import { DateRangeSection } from './filters/DateRangeSection';
import { CompanySection } from './filters/CompanySection';
import { SupplierSection } from './filters/SupplierSection';
import { ClassificationSection } from './filters/ClassificationSection';
import { CallOffSection } from './filters/CallOffSection';
import { ContractSection } from './filters/ContractSection';
import { SectionDivider } from '../filters/FilterSection';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useFilterVisibility } from '../../hooks/useFilterVisibility';
import { COST_VIEW_DISABLED_HINT, COST_VIEW_RANGES, costViewToDateBounds, type CostView } from './costViewRanges';
import { Button } from '../ui/button';
import { X, Save, Search } from 'lucide-react';

// Re-export the shared date helpers so existing importers keep resolving them
// here after the CR-6352 split into dashboardFilterUtils / useDashboardFilters.
export { formatLocalDate, getLast12MonthsRange } from './dashboardFilterUtils';


interface DashboardFiltersProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  suppliers: Array<{ id: number; name: string; group?: number | null; country?: string | null }>;
  customers: Array<{ id: number; name: string }>;
  sites: Array<{ id: number; name: string; country?: string; group_id?: number | null; group?: number | null }>;
  warehouses: Array<{ id: number; name: string; site: number }>;
  supplierGroups: Array<{ id: number; name: string }>;
  commodityGroups: Array<{ id: number; name: string }>;
  commodityTypes: Array<{ id: number; name: string; commodity_group?: number }>;
  // CR-4349: Division filter options (only shown when > 0)
  divisions: Array<{ id: number; name: string }>;
  preferencesCurrency: string;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  onShowSaveDialog: () => void;
  onAutoCollapse?: () => void;
  // CR-4800: hide purchasing/logistics filters on the Compliance page and
  // surface approval-stage + risk filters instead.
  isComplianceMode?: boolean;
  /** CR-5454: the Cost tab adds Next 20 weeks and the Contract filters. */
  isCostTab?: boolean;
  /** CR-8611: the Cost sub-tab on screen — it decides which date ranges may be picked. */
  costView?: CostView;
}

/**
 * CR-6401: the dashboard filter panel, grouped into labelled sections.
 *
 * Previously four undifferentiated grid rows in which related filters were
 * scattered — Division alone in its own row, the Historical/Live toggle beside
 * the strategic-suppliers tickbox, Commodity Group and Commodity Type split
 * across rows despite cascading into one another. This composes four section
 * components (Date Range / Company / Supplier / Classification) and owns only
 * the footer. Filter behaviour, input types and every cascade are unchanged.
 *
 * CR-6402 rework: the supplier portal renders exactly the Supplier View
 * design — Date Range · Customer · Supplier — so Classification is customer
 * portal only, and the Supplier group carries Supplier group / Supplier
 * country / Supplier site (see SupplierSection for the fields it drops).
 */
export function DashboardFilters({
  filters,
  setFilters,
  suppliers,
  customers: _customers,
  sites,
  warehouses = [],
  supplierGroups = [],
  commodityGroups = [],
  commodityTypes = [],
  divisions = [],
  preferencesCurrency: _preferencesCurrency,
  onApplyFilters,
  onResetFilters,
  onShowSaveDialog,
  onAutoCollapse: _onAutoCollapse,
  isComplianceMode = false,
  isCostTab = false,
  costView = 'future',
}: DashboardFiltersProps) {
  const {
    portalIsSupplier, scopedDivisions, isDivisionLocked, isSiteLocked, lockedDivisionValue,
    siteCountryOptions, supplierCountryOptions, filteredSites, filteredWarehouses,
    activeQuickRange, isLast12MonthsActive,
    setQuickDateRange, handleSiteCountryChange, handleSupplierCountryChange,
  } = useDashboardFilters({ filters, setFilters, sites, suppliers, warehouses, divisions });

  const rangeSummary = describeActiveRange(activeQuickRange, isLast12MonthsActive, filters);

  // The call-off basis toggle only means anything to a company that actually
  // has call-off suppliers. `switch_call_off_tab` already resolves to "this
  // company has at least one active relationship with call_off_enabled", so
  // the whole section is hidden rather than shown inert. Compliance mode has
  // no delivery metrics to re-base, so it stays out there too.
  const { isFeatureEnabled } = useMenuSettings();
  // CR-4802: the tenant's declared/detected filter shape gates every control below.
  const fv = useFilterVisibility();
  const showCallOffBasis = !isComplianceMode && isFeatureEnabled('switch_call_off_tab');

  // CR-4802: dynamic filter visibility — Warehouse and Company Country only
  // matter when the tenant's data actually varies along that axis.
  const showWarehouse = !isComplianceMode && warehouses.length > 0 && fv.warehouse;
  const showCompanyCountry = siteCountryOptions.length > 1 && fv.companyCountry;

  // CR-5868 UI feedback: the panel used the section shell's own full-bleed
  // rules, so every divider ran into the card border and the one above Date
  // Range doubled up with the card header's edge. Switched to the layout the
  // Purchasing → PO Headers tab already uses: the shell's rules are turned off
  // and an inset SectionDivider sits above each rendered section, so a
  // conditional section brings its own divider and no two ever stack.
  return (
    <div className="bg-card [&>section]:border-t-0">
      <SectionDivider />

      <DateRangeSection
        filters={filters}
        setFilters={setFilters}
        activeQuickRange={activeQuickRange}
        isLast12MonthsActive={isLast12MonthsActive}
        setQuickDateRange={setQuickDateRange}
        showDataMode={!isComplianceMode && fv.historicalLive}
        showNext20Weeks={isCostTab}
        allowedRanges={isCostTab ? COST_VIEW_RANGES[costView] : undefined}
        disabledHint={isCostTab ? COST_VIEW_DISABLED_HINT[costView] : undefined}
        dateBounds={isCostTab ? costViewToDateBounds(costView) : undefined}
      />

      <SectionDivider />

      <CompanySection
        filters={filters}
        setFilters={setFilters}
        divisions={divisions}
        scopedDivisions={scopedDivisions}
        isDivisionLocked={isDivisionLocked}
        lockedDivisionValue={lockedDivisionValue}
        isSiteLocked={isSiteLocked}
        siteCountryOptions={siteCountryOptions}
        filteredSites={filteredSites}
        filteredWarehouses={filteredWarehouses}
        showWarehouse={showWarehouse}
        showCountry={showCompanyCountry}
        showDivision={fv.division}
        // CR-6402: on the supplier portal this group describes the customers
        // the supplier trades with, so the supplier-view design labels it
        // "Customer" rather than "Company".
        title={portalIsSupplier ? 'Customer' : 'Company'}
        onSiteCountryChange={handleSiteCountryChange}
      />

      <SectionDivider />

      <SupplierSection
        filters={filters}
        setFilters={setFilters}
        suppliers={suppliers}
        supplierGroups={supplierGroups}
        supplierCountryOptions={supplierCountryOptions}
        onSupplierCountryChange={handleSupplierCountryChange}
        supplierPortal={portalIsSupplier}
        showPurchasingFilters={!isComplianceMode}
        showSupplierCode={fv.supplierCode}
      />

      {/* CR-6402 rework: Classification (Supplier type, Commodity group,
          Commodity type) is not in the Supplier View design — customer portal
          only. Compliance mode is a customer-portal mode, so its Approval
          stage / Risk category filters are unaffected. */}
      {!portalIsSupplier && (
        <>
          <SectionDivider />
          <ClassificationSection
            filters={filters}
            setFilters={setFilters}
            commodityGroups={commodityGroups}
            commodityTypes={commodityTypes}
            showPurchasingFilters={!isComplianceMode}
            isComplianceMode={isComplianceMode}
            showSupplierType={fv.supplierType}
            showCommodity={fv.commodity}
          />
        </>
      )}

      {/* CR-5454: contract terms are the customer's commercial data, so the
          Contract filters are customer portal only (never the supplier view). */}
      {isCostTab && !portalIsSupplier && (
        <>
          <SectionDivider />
          <ContractSection filters={filters} setFilters={setFilters} />
        </>
      )}

      {showCallOffBasis && (
        <>
          <SectionDivider />
          <CallOffSection filters={filters} setFilters={setFilters} />
        </>
      )}

      {/* Filter footer: Clear All (left), what the results cover (centre),
          Apply Filters (right) — the diagram's layout. Save Filter is kept
          alongside Clear All: it isn't in the mockup but is existing
          functionality the ticket requires be preserved.
          CR-6402 rework: the same shared Button sizes, icons and labels as the
          PO Lines footer (CustomerSectionedFilters). */}
      {/* No divider above the footer — the PO Headers panel ends its last
          section and goes straight into Clear All / Save Filter / Apply. */}
      <div className="px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onResetFilters} className="px-6">
              <X className="h-4 w-4 mr-2" />
              Clear All
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onShowSaveDialog}
              className="px-6 border-primary text-primary hover:bg-primary/10 hover:text-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Filter
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">Showing results for {rangeSummary}</p>

          <Button type="button" onClick={onApplyFilters} className="px-6">
            <Search className="h-4 w-4 mr-2" />
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
}
